import { Mark } from '@tiptap/react';

/**
 * FontSize extension for Tiptap that handles text size variations.
 *
 * This extension manages font sizes in two ways:
 * 1. For display: Uses Tailwind classes (text-sm, text-base, etc.) applied directly to spans for display purposes.
 * 2. For storage: Uses data-size attributes (sm, base, lg, etc.) to persist the logical size,
 *    which is helpful for reflecting the class size in the editor's toolbar.
 *
 * The extension also supports parsing existing content by:
 * - Reading data-size attributes directly
 * - Converting rem-based font-size styles to logical sizes (0.875rem -> sm, 1rem -> base, etc.)
 *
 * When rendering, it only applies classes/attributes when the size differs from 'base',
 * keeping the HTML clean and minimal.
 */

export const FONT_SIZES = [
  { name: 'Small', value: 'sm' },
  { name: 'Base', value: 'base' },
  { name: 'Large', value: 'lg' },
  { name: 'XL', value: 'xl' },
  { name: '2XL', value: '2xl' },
  { name: '3XL', value: '3xl' },
];

type FontSize = 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';
export const FontSize = Mark.create({
  name: 'fontSize',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      size: {
        default: 'base' as FontSize,
        parseHTML: (element) => {
          const dataSize = element.getAttribute('data-size');
          if (dataSize) {
            return dataSize as FontSize;
          }
          const style = element.getAttribute('style') || '';
          const fontSizeMatch = style.match(/font-size\s*:\s*([\d.]+rem)/i);
          if (fontSizeMatch) {
            const sizeMap = {
              '0.875rem': 'sm',
              '1rem': 'base',
              '1.125rem': 'lg',
              '1.25rem': 'xl',
              '1.5rem': '2xl',
              '1.75rem': '3xl',
            } as const;
            return sizeMap[fontSizeMatch[1] as keyof typeof sizeMap] || 'base';
          }
          return 'base';
        },
        renderHTML: (attributes) => {
          if (!attributes.size || attributes.size === 'base') {
            return {};
          }
          const tailwindMap = {
            sm: 'text-sm',
            base: 'text-base',
            lg: 'text-lg',
            xl: 'text-xl',
            '2xl': 'text-2xl',
            '3xl': 'text-3xl',
          } as const;
          return {
            'data-size': attributes.size,
            class: tailwindMap[attributes.size as FontSize] || 'text-base',
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-size]',
      },
      {
        tag: 'span',
        getAttrs: (element) => {
          const style = element.getAttribute('style') || '';
          return /font-size\s*:\s*[\d.]+rem/i.test(style) ? {} : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0];
  },
});
